import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, Check, X, Clock, Download, Upload } from "lucide-react";
import { useRef } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDivision } from "@/context/DivisionContext";
import { formatINRShort } from "@/lib/constants";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const SALES_ROLES = ["sales_manager", "sales_coordinator", "salesperson", "bd", "dealer"];

function computeDownline(users, rootId) {
  const childrenOf = {};
  users.forEach((u) => { (childrenOf[u.manager_id] ||= []).push(u.id); });
  const result = new Set();
  let frontier = [rootId];
  while (frontier.length) {
    const next = [];
    frontier.forEach((id) => (childrenOf[id] || []).forEach((cid) => {
      if (!result.has(cid)) { result.add(cid); next.push(cid); }
    }));
    frontier = next;
  }
  return result;
}

function TargetTemplateButtons() {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/targets-template/download", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = (res.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/)?.[1] || "Target break-up.xlsx";
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/targets-template/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.rows} target rows · ${data.total_cr} Cr${data.unmatched?.length ? ` · unmatched: ${data.unmatched.join(", ")}` : ""}`);
      qc.invalidateQueries({ queryKey: ["sales-bi"] });
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <>
      <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={download} disabled={busy} data-testid="target-template-download">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Break-up template
      </Button>
      <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="target-template-import">
        <Upload className="h-4 w-4" /> Import break-up
      </Button>
      <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={upload} data-testid="target-template-file" />
    </>
  );
}

export default function Targets() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { divisionParam } = useDivision();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [edits, setEdits] = useState({});
  const years = [now.getFullYear(), now.getFullYear() + 1, now.getFullYear() - 1];

  const isApprover = ["super_admin", "sales_head"].includes(user?.role);
  const isManager = user?.role === "sales_manager";

  const { data: users = [] } = useQuery({
    queryKey: ["users", divisionParam],
    queryFn: async () => (await api.get("/users", { params: { division_id: divisionParam } })).data,
  });
  const { data: targets = [], isLoading } = useQuery({
    queryKey: ["targets", divisionParam, year],
    queryFn: async () => (await api.get("/targets", { params: { division_id: divisionParam, year } })).data,
  });
  const { data: pending = [] } = useQuery({
    queryKey: ["targets-pending", divisionParam],
    queryFn: async () => (await api.get("/targets/pending")).data,
    enabled: isApprover,
  });

  const reps = (() => {
    let r = users.filter((u) => SALES_ROLES.includes(u.role) && u.is_active !== false);
    if (isManager && user?.id) {
      const down = computeDownline(users, user.id);
      r = r.filter((u) => down.has(u.id));
    }
    return r;
  })();

  const tmeta = {};
  targets.forEach((t) => { tmeta[`${t.user_id}_${t.month}`] = t; });

  useEffect(() => { setEdits({}); }, [year, month, divisionParam]);

  const stored = (uid, m) => {
    const t = tmeta[`${uid}_${m}`];
    if (!t) return "";
    return t.status === "pending_approval" ? (t.proposed_amount ?? "") : (t.amount ?? "");
  };
  const val = (uid, m) => {
    const key = `${uid}_${m}`;
    return edits[key] !== undefined ? edits[key] : stored(uid, m);
  };
  const setVal = (uid, m, v) => setEdits((e) => ({ ...e, [`${uid}_${m}`]: v }));

  const saveRow = async (uid) => {
    try {
      await api.post("/targets", { user_id: uid, year, month: 0, amount: Number(val(uid, 0)) || 0 });
      await api.post("/targets", { user_id: uid, year, month, amount: Number(val(uid, month)) || 0 });
      qc.invalidateQueries({ queryKey: ["targets"] });
      qc.invalidateQueries({ queryKey: ["targets-pending"] });
      toast.success(isManager ? "Target sent to National Sales Head for approval" : "Targets saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  const decide = async (id, action) => {
    try {
      await api.post(`/targets/${id}/decide`, { action });
      qc.invalidateQueries({ queryKey: ["targets-pending"] });
      qc.invalidateQueries({ queryKey: ["targets"] });
      toast.success(action === "approve" ? "Target approved" : "Target rejected");
    } catch (e) { toast.error(apiError(e)); }
  };

  const StatusTag = ({ uid, m }) => {
    const t = tmeta[`${uid}_${m}`];
    if (!t) return null;
    if (t.status === "pending_approval")
      return <Badge variant="outline" className="mt-1 gap-1 border-amber-300 bg-amber-50 text-amber-700 text-[10px]"><Clock className="h-2.5 w-2.5" /> Pending approval</Badge>;
    if (t.status === "rejected")
      return <Badge variant="outline" className="mt-1 gap-1 border-red-300 bg-red-50 text-red-700 text-[10px]"><X className="h-2.5 w-2.5" /> Rejected</Badge>;
    return null;
  };

  return (
    <div>
      <PageHeader
        title="Sales Targets"
        subtitle={isManager
          ? "Propose booking targets for your team — they route to the National Sales Head for approval."
          : "Set yearly and monthly booking targets per salesperson. Used by the Sales Summary dashboard."}
      >
        {["super_admin", "sales_head", "management"].includes(user?.role) && <TargetTemplateButtons />}
        <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
          <SelectTrigger className="h-9 w-[110px]" data-testid="targets-month"><SelectValue /></SelectTrigger>
          <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="h-9 w-[100px]" data-testid="targets-year"><SelectValue /></SelectTrigger>
          <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </PageHeader>

      {isApprover && pending.length > 0 && (
        <Card className="mb-6 overflow-hidden border-amber-200" data-testid="pending-approvals-card">
          <div className="px-4 py-3 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
            <Clock className="h-4 w-4 text-amber-600" />
            <h3 className="font-heading font-semibold text-sm text-amber-800">Target Approvals ({pending.length})</h3>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {["Salesperson", "Period", "Proposed", "Proposed By"].map((h) => (
                    <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>
                  ))}
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending.map((p) => (
                  <TableRow key={p.id} data-testid={`pending-row-${p.id}`}>
                    <TableCell className="font-medium">{p.user_name}</TableCell>
                    <TableCell className="text-slate-600">{p.year}{p.month ? ` · ${MONTHS[p.month - 1]}` : " · Yearly"}</TableCell>
                    <TableCell className="font-mono font-semibold text-slate-900">{formatINRShort(p.proposed_amount)}</TableCell>
                    <TableCell className="text-slate-600">{p.proposed_by_name}</TableCell>
                    <TableCell>
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" className="gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => decide(p.id, "approve")} data-testid={`approve-${p.id}`}><Check className="h-3.5 w-3.5" /> Approve</Button>
                        <Button size="sm" variant="outline" className="gap-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => decide(p.id, "reject")} data-testid={`reject-${p.id}`}><X className="h-3.5 w-3.5" /> Reject</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Salesperson</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Yearly Target (INR)</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">{MONTHS[month - 1]} Target (INR)</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reps.map((u) => (
                  <TableRow key={u.id} data-testid={`target-row-${u.id}`}>
                    <TableCell><p className="font-medium">{u.name}</p><p className="text-xs text-slate-400 capitalize">{u.role.replace(/_/g, " ")}</p></TableCell>
                    <TableCell>
                      <Input type="number" className="h-9 w-40" value={val(u.id, 0)} onChange={(e) => setVal(u.id, 0, e.target.value)} placeholder="0" data-testid={`target-yearly-${u.id}`} />
                      <StatusTag uid={u.id} m={0} />
                    </TableCell>
                    <TableCell>
                      <Input type="number" className="h-9 w-40" value={val(u.id, month)} onChange={(e) => setVal(u.id, month, e.target.value)} placeholder="0" data-testid={`target-month-${u.id}`} />
                      <StatusTag uid={u.id} m={month} />
                    </TableCell>
                    <TableCell>
                      <Button size="sm" onClick={() => saveRow(u.id)} className="gap-1.5 bg-slate-900 hover:bg-slate-800" data-testid={`target-save-${u.id}`}>
                        <Save className="h-3.5 w-3.5" /> {isManager ? "Propose" : "Save"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!reps.length && <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8">No salespeople found.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
